import React, { useState } from 'react';
import { 
  Smartphone, Monitor, Apple, Download, CheckCircle2, 
  Sparkles, ShieldCheck, Zap, ArrowDownToLine, Layers, Grid
} from 'lucide-react';

export const DownloadAppsPage: React.FC = () => {
  const [downloadSuccess, setDownloadSuccess] = useState('');

  const fullApps = [
    {
      title: 'AbilityOS Full Suite Enterprise Android App (.APK)',
      platform: 'Android 8.0+ (ARM64 & x86_64)',
      version: 'v1.0.5 Enterprise Build',
      size: '26.4 MB',
      filename: 'AbilityOS_Android_Enterprise_v1.0.apk',
      icon: Smartphone,
      color: 'bg-emerald-500 text-white'
    },
    {
      title: 'AbilityOS Full Suite Windows Desktop App (.EXE)',
      platform: 'Windows 10 / 11 (64-bit)',
      version: 'v1.0.5 Setup Installer',
      size: '44.2 MB',
      filename: 'AbilityOS_Windows_Enterprise_v1.0_Setup.exe',
      icon: Monitor,
      color: 'bg-blue-600 text-white'
    },
    {
      title: 'AbilityOS Full Suite macOS App (.DMG)',
      platform: 'macOS 12.0+ (Apple Silicon M1/M2/M3/M4 & Intel)',
      version: 'v1.0.5 Universal Disk Image',
      size: '50.1 MB',
      filename: 'AbilityOS_macOS_Enterprise_v1.0.dmg',
      icon: Apple,
      color: 'bg-slate-900 text-white'
    }
  ];

  const moduleApps = [
    { id: 'aac', name: 'AAC Speech Synthesizer Module', filePrefix: 'AAC_SpeechSynthesizer', desc: 'Augmentative speech board & TTS voice engine' },
    { id: 'speech-therapy', name: 'Speech Therapy & Articulation Module', filePrefix: 'SpeechTherapy_Articulation', desc: 'Phonetic drills & vocal spectrogram visualizer' },
    { id: 'autism', name: 'Autism & ABA Behavior Module', filePrefix: 'Autism_ABA_Behavior', desc: 'ABC incident logger & sensory trigger tracker' },
    { id: 'sign-language', name: 'Sign Language & Gestures Module', filePrefix: 'SignLanguage_Gestures', desc: 'Indian Sign Language (ISL) video dictionary' },
    { id: 'braille', name: 'Braille & Tactile Learning Module', filePrefix: 'Braille_Tactile_Learning', desc: '6-dot Nemeth Braille translator & screen reader' },
    { id: 'student', name: 'Student & IEP Progress Module', filePrefix: 'Student_IEP_Progress', desc: 'SMART IEP goal builder & milestone progress' },
    { id: 'therapist', name: 'Therapist Workspace & SOAP Notes', filePrefix: 'Therapist_SOAP_Notes', desc: 'SOAP note editor & clinical case history' },
    { id: 'teacher', name: 'Teacher Classroom & Lesson Module', filePrefix: 'Teacher_Classroom_Lesson', desc: 'Differentiated lesson plan builder' },
    { id: 'assessment', name: 'Diagnostic Assessment Module', filePrefix: 'Diagnostic_Clinical_Assessment', desc: 'Sensory Profile 2 & CARS-2 evaluators' },
    { id: 'school-erp', name: 'Special School ERP & Timetable', filePrefix: 'SpecialSchool_ERP_Timetable', desc: 'Wheelchair bus GPS route tracker & rosters' },
    { id: 'hospital', name: 'Rehabilitation Hospital Module', filePrefix: 'RehabHospital_PatientCare', desc: 'Inpatient ward bed manager & care rounds' },
    { id: 'billing', name: 'NDIS & Insurance Billing Module', filePrefix: 'NDIS_Clinical_Billing', desc: 'NDIS code catalog & automated claim engine' },
    { id: 'communication', name: 'Parent Communication Module', filePrefix: 'ParentComm_Messenger', desc: 'Secure team messaging & daily photo gallery' },
    { id: 'inventory', name: 'Assistive Device Inventory Module', filePrefix: 'AssistiveDevice_Inventory', desc: 'Wheelchair & hearing aid hardware lender' },
    { id: 'analytics', name: 'Executive Analytics BI Module', filePrefix: 'ExecutiveAnalytics_BI', desc: 'Institutional compliance gauges & audit reports' },
    { id: 'ai', name: 'AI Copilot & Report Summarizer', filePrefix: 'AICopilot_ReportSummarizer', desc: 'Automated SMART goal prompts & note summarizer' },
    { id: 'sensory-room', name: 'Sensory Room & Snoezelen Controller', filePrefix: 'SensoryRoom_Snoezelen_Controller', desc: 'Interactive lighting presets & ambient soundscapes' },
    { id: 'tele-rehab', name: 'Tele-Rehabilitation Video Therapy', filePrefix: 'TeleRehab_RemoteVideo_Therapy', desc: 'Encrypted HD video consultations & session chat' },
    { id: 'early-intervention', name: 'Early Intervention Milestone Tracker', filePrefix: 'EarlyIntervention_Developmental_Tracker', desc: 'Pediatric developmental checklists (0-36 Months)' },
    { id: 'vocational-living', name: 'Vocational & Life Skills Module', filePrefix: 'Vocational_IndependentLiving_Skills', desc: 'Adult disability work readiness & life skills' },
    { id: 'hydrotherapy', name: 'Hydrotherapy & Aquatic Physical Therapy', filePrefix: 'Hydrotherapy_AquaticRehab_Controller', desc: 'Pool temperature & aquatic buoyancy exercises' },
    { id: 'music-therapy', name: 'Music & Auditory Stimulation Therapy', filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation', desc: 'Auditory motor entrainment metronome & rhythm' },
    { id: 'animal-therapy', name: 'Animal-Assisted Therapy & Pet Care', filePrefix: 'AnimalAssisted_Therapy_PetCare', desc: 'Therapy dog scheduling & stress reduction scores' },
    { id: 'prosthetics', name: 'Prosthetics & Exoskeleton Fitting', filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting', desc: 'Bionic limb telemetry & gait analysis' },
    { id: 'vbmapp-assessment', name: 'VB-MAPP & ABLLS-R Behavioral Protocol', filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol', desc: 'Milestone grid scoring & barrier analysis' },
    { id: 'core-vocab-ai', name: 'Motor Core Vocabulary & AI Predictor', filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor', desc: 'Fixed Crescendo motor-planning grid & AI word prediction' },
    { id: 'fim-weefim', name: 'FIM & WeeFIM Clinical Scoring', filePrefix: 'FIM_WeeFIM_Clinical_Scoring', desc: '18-item 7-level functional independence scorecard' },
    { id: 'evv-gps-tracker', name: 'EVV Visit Verification & GPS', filePrefix: 'EVV_GPS_Caregiver_CheckIn', desc: 'Browser GPS coordinates & digital canvas signature' },
    { id: 'gamified-neuro', name: 'WebGL Gamified Biofeedback Neuro-Rehab', filePrefix: 'Gamified_WebGL_Neuro_Rehab', desc: 'Interactive WebGL target tracking & reaction latency' },
    { id: 'transition-matrix', name: 'Secondary Transition & Vocational Matrix', filePrefix: 'Secondary_Transition_Vocational_Matrix', desc: 'IDEA Sec 300.320 career cards & post-secondary goals' }
  ];

  const handleDownload = (filename: string, appName: string) => {
    setDownloadSuccess(`Downloading ${appName}... Check your downloads folder!`);
    
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setDownloadSuccess('');
    }, 4000);
  };

  return (
    <div className="py-10 px-6 max-w-7xl mx-auto space-y-12 bg-slate-50 min-h-screen">
      
      {/* Header Banner */}
      <div className="text-center space-y-4 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs font-extrabold uppercase tracking-wider">
          <Smartphone className="w-4 h-4 text-emerald-600" />
          <span>Standalone App Installer Hub (93 Apps Ready)</span>
        </div>

        <h1 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
          Download Dedicated AbilityOS Apps (Android, Windows, Mac)
        </h1>

        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
          Every module is packaged as an independent, dedicated application for Android tablets, Windows PCs, and macOS workstations. Built over our high-speed live server for instant real-time synchronization.
        </p>

        {downloadSuccess && (
          <div className="p-4 bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-bold rounded-2xl shadow-sm flex items-center justify-center gap-2 animate-bounce">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>{downloadSuccess}</span>
          </div>
        )}
      </div>

      {/* FULL PLATFORM SUITE DOWNLOAD CARDS */}
      <div className="space-y-4">
        <h2 className="text-xl font-black text-slate-900 flex items-center gap-2 border-b pb-3">
          <Layers className="w-5 h-5 text-blue-600" />
          Full Platform Suite Installers (Contains All 30 Modules)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {fullApps.map((app, idx) => {
            const Icon = app.icon;
            return (
              <div key={idx} className="bg-white rounded-3xl border border-slate-200 p-6 shadow-lg hover:shadow-2xl transition duration-300 flex flex-col justify-between space-y-6">
                <div className="space-y-4">
                  <div className={`w-14 h-14 ${app.color} rounded-2xl flex items-center justify-center shadow-md`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 leading-snug">{app.title}</h3>
                    <p className="text-xs text-slate-500 mt-1 font-medium">{app.platform}</p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-600 bg-slate-50 p-3 rounded-xl font-mono">
                    <span>{app.version}</span>
                    <span className="font-bold text-blue-600">{app.size}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleDownload(app.filename, app.title)}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl text-xs shadow-md transition flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Full App</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* 30 DEDICATED MODULE APPS (90 TOTAL INSTALLERS) */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3">
          <div>
            <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
              <Grid className="w-5 h-5 text-amber-500" />
              Dedicated 30-Module App Packages (90 Apps Total)
            </h2>
            <p className="text-xs text-slate-500">Download standalone installer binaries specifically for individual modules.</p>
          </div>
          <span className="text-xs font-bold bg-amber-100 text-amber-800 px-3 py-1 rounded-full border border-amber-300">
            90 Binary Packages Active
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {moduleApps.map((m, idx) => {
            const numStr = (idx + 1).toString().padStart(2, '0');
            return (
              <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition space-y-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-extrabold font-mono px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                      MODULE {numStr}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold">Standalone</span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 leading-snug">{m.name}</h3>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">{m.desc}</p>
                </div>

                <div className="pt-3 border-t border-slate-100 space-y-1.5">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Download Installer:</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_Android.apk`, `${m.name} Android APK`)}
                      className="py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1"
                      title="Download Android APK"
                    >
                      <Smartphone className="w-3 h-3 text-emerald-600" />
                      <span>.APK</span>
                    </button>

                    <button
                      onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_Windows_Setup.exe`, `${m.name} Windows EXE`)}
                      className="py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-300 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1"
                      title="Download Windows EXE"
                    >
                      <Monitor className="w-3 h-3 text-blue-600" />
                      <span>.EXE</span>
                    </button>

                    <button
                      onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_macOS.dmg`, `${m.name} macOS DMG`)}
                      className="py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1"
                      title="Download macOS DMG"
                    >
                      <Apple className="w-3 h-3 text-slate-700" />
                      <span>.DMG</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};
